export const translations = {
  en: {
    meta: {
      title: 'Thevenel Joazard | Software Engineer',
      description:
        'Thevenel Joazard is a software engineer apprentice building reliable backend systems, data-processing workflows, and software products with Java and Python.'
    },

    nav: {
      projects: 'Projects',
      experience: 'Experience',
      about: 'About',
      contact: 'Contact',
      resume: 'Résumé'
    },

    hero: {
      eyebrow: 'Software Engineer Apprentice · Engineering Student',
      title: 'I build reliable software systems.',
      description:
        'I develop backend applications, data-processing workflows, and automation tools with Java, Spring Boot, Python, and SQL.',
      projectsCta: 'View selected projects',
      resumeCta: 'Download résumé',
      availability:
        'Available full-time for a 13-week international Software Engineering internship: Jun 21 – Sep 19, 2027.'
    },

    proof: {
      automation: 'manual workflow automated at Thales',
      coverage: 'automated test coverage at Thales',
      languages: 'languages spoken'
    },

    projects: {
      eyebrow: 'Selected work',
      title: 'Projects that show how I engineer software',
      description:
        'A focused selection covering product architecture, algorithms, systems programming, backend engineering, and software reliability.'
    },

    experience: {
      eyebrow: 'Professional experience',
      title: 'Building and modernizing real systems'
    },

    skills: {
      eyebrow: 'Technical foundation',
      title: 'Tools and concepts supported by practical experience'
    },

    about: {
      eyebrow: 'About',
      title: 'Engineering software as useful infrastructure',

      paragraph1:
        'Growing up in Ouanaminthe, Haiti, I wanted to build infrastructure that could improve everyday life. Discovering computer science changed my idea of infrastructure: software could also help schools, businesses, hospitals, and public services operate more reliably.',

      paragraph2:
        'I enjoy understanding software at different levels, from instruction execution and memory state to backend applications, data-processing systems, and the domain decisions that keep long-lived software maintainable.',

      paragraph3:
        'My academic and professional journey includes Haiti, the Dominican Republic, France, Germany, and Italy. I speak five languages and expect to graduate from IMT Nord Europe in September 2028.'
    },

    community: {
      title: 'Community and knowledge sharing',
      python: 'Contributor to French translations of Python documentation.',
      pycon: 'Presented a practical Wagtail tutorial at PyConFR.',
      youtube:
        'Create technology education in Haitian Creole for an audience of about 4,000 subscribers.'
    },

    contact: {
      eyebrow: 'Contact',
      title: 'Let’s discuss software engineering and international opportunities.',
      description: 'The fastest way to reach me is by email or LinkedIn.',
      resume: 'Download résumé'
    },

    footer: {
      location: 'Lille, France · Built with Parcel and Bootstrap'
    }
  },

  fr: {
    meta: {
      title: 'Thevenel Joazard | Ingénieur logiciel',
      description:
        'Thevenel Joazard est ingénieur logiciel en alternance et conçoit des systèmes backend fiables, des traitements de données et des produits logiciels avec Java et Python.'
    },

    nav: {
      projects: 'Projets',
      experience: 'Expérience',
      about: 'À propos',
      contact: 'Contact',
      resume: 'CV'
    },

    hero: {
      eyebrow: 'Ingénieur logiciel en alternance · Étudiant ingénieur',
      title: 'Je développe des systèmes logiciels fiables.',
      description:
        'Je conçois des applications backend, des traitements de données et des outils d’automatisation avec Java, Spring Boot, Python et SQL.',
      projectsCta: 'Découvrir mes projets',
      resumeCta: 'Télécharger mon CV',
      availability:
        'Disponible à temps plein pour un stage international de 13 semaines en génie logiciel : du 21 juin au 19 septembre 2027.'
    },

    proof: {
      automation: 'processus manuel automatisé chez Thales',
      coverage: 'de couverture de tests automatisés chez Thales',
      languages: 'langues parlées'
    },

    projects: {
      eyebrow: 'Travaux sélectionnés',
      title: 'Des projets qui illustrent ma manière de concevoir des logiciels',
      description:
        'Une sélection centrée sur l’architecture produit, l’algorithmique, la programmation système, le backend et la fiabilité logicielle.'
    },

    experience: {
      eyebrow: 'Expérience professionnelle',
      title: 'Développer et moderniser des systèmes réels'
    },

    skills: {
      eyebrow: 'Fondements techniques',
      title: 'Des outils et concepts soutenus par une expérience pratique'
    },

    about: {
      eyebrow: 'À propos',
      title: 'Concevoir le logiciel comme une infrastructure utile',

      paragraph1:
        'En grandissant à Ouanaminthe, en Haïti, je voulais construire des infrastructures capables d’améliorer la vie quotidienne. La découverte de l’informatique a élargi cette vision : le logiciel peut lui aussi aider les écoles, les entreprises, les hôpitaux et les services publics à fonctionner de manière plus fiable.',

      paragraph2:
        'J’aime comprendre les logiciels à différents niveaux, de l’exécution des instructions et de la gestion de la mémoire jusqu’aux applications backend, aux systèmes de traitement de données et aux décisions de domaine qui rendent un logiciel durable et maintenable.',

      paragraph3:
        'Mon parcours académique et professionnel comprend Haïti, la République dominicaine, la France, l’Allemagne et l’Italie. Je parle cinq langues et mon diplôme d’ingénieur à IMT Nord Europe est prévu en septembre 2028.'
    },

    community: {
      title: 'Communauté et partage de connaissances',
      python: 'Contribution à la traduction française de la documentation Python.',
      pycon:
        'Présentation d’un tutoriel pratique consacré à Wagtail lors de PyConFR.',
      youtube:
        'Création de contenu technologique en créole haïtien pour une audience d’environ 4 000 abonnés.'
    },

    contact: {
      eyebrow: 'Contact',
      title:
        'Échangeons autour du génie logiciel et des opportunités internationales.',
      description:
        'Les moyens les plus directs pour me contacter sont l’e-mail et LinkedIn.',
      resume: 'Télécharger mon CV'
    },

    footer: {
      location: 'Lille, France · Développé avec Parcel et Bootstrap'
    }
  }
};

export const projects = {
  en: [
    {
      title: 'Student Information System',
      subtitle: 'Product architecture, auditability, and access control',
      description:
        'Designed and built a modular Django/PostgreSQL system that preserves student lifecycle history across admissions, institutional and national identifiers, enrollment transitions, staff accounts, RBAC, and versioned private photo records. Uses transactional service boundaries, protected media delivery, and audit histories; the current release is validated by 563 automated tests.',
      technologies: ['Python', 'Django', 'PostgreSQL', 'Docker'],
      status: 'Private product · 563 tests'
    },

    {
      title: 'Java Ray Tracer',
      subtitle: 'Algorithms, data structures, and computer graphics',
      description:
        'Built a 3D rendering engine from scratch with scene parsing, Lambert and Blinn-Phong shading, shadows, reflections, BVH spatial acceleration to reduce unnecessary primitive intersection tests, PNG export, and more than 80% test coverage.',
      technologies: ['Java', 'JavaFX', 'JUnit', 'BVH'],
      status: 'Academic project'
    },

    {
      title: 'RISC-V Emulator',
      subtitle: 'Systems programming in Rust',
      description:
        'Implemented RV32I binary loading, instruction decoding, step-by-step execution, program-counter management, registers, memory state, structured error handling, and automated tests.',
      technologies: [
        'Rust',
        'RISC-V',
        'Computer Architecture',
        'Testing'
      ],
      status: 'Academic project'
    },

    {
      title: 'GachaGame Microservices',
      subtitle: 'Distributed backend architecture',
      description:
        'Designed authentication and player services in a four-person team using REST APIs, separate PostgreSQL databases, OpenFeign communication, domain rules, and Docker orchestration.',
      technologies: ['Java', 'Spring Boot', 'PostgreSQL', 'Docker'],
      status: 'Team project'
    }
  ],

  fr: [
    {
      title: 'Student Information System',
      subtitle: 'Architecture produit, auditabilité et contrôle d’accès',
      description:
        'Conception et développement d’un système modulaire Django/PostgreSQL préservant l’historique du cycle de vie de l’élève : admissions, identifiants institutionnels et nationaux, transitions d’inscription, comptes utilisateurs, RBAC et historique versionné des photos privées. Utilise des frontières de services transactionnelles, une diffusion protégée des médias et des historiques d’audit ; la version actuelle est validée par 563 tests automatisés.',
      technologies: ['Python', 'Django', 'PostgreSQL', 'Docker'],
      status: 'Produit privé · 563 tests'
    },

    {
      title: 'Ray Tracer en Java',
      subtitle:
        'Algorithmique, structures de données et informatique graphique',
      description:
        'Développement intégral d’un moteur de rendu 3D avec parsing de scènes, modèles de Lambert et Blinn-Phong, ombres, réflexions, accélération spatiale BVH pour réduire les tests d’intersection inutiles, export PNG et plus de 80 % de couverture de tests.',
      technologies: ['Java', 'JavaFX', 'JUnit', 'BVH'],
      status: 'Projet académique'
    },

    {
      title: 'Émulateur RISC-V',
      subtitle: 'Programmation système en Rust',
      description:
        'Implémentation du chargement de binaires RV32I, du décodage d’instructions, de l’exécution pas à pas, du compteur ordinal, des registres, de la mémoire, de la gestion structurée des erreurs et de tests automatisés.',
      technologies: [
        'Rust',
        'RISC-V',
        'Architecture des ordinateurs',
        'Tests'
      ],
      status: 'Projet académique'
    },

    {
      title: 'GachaGame Microservices',
      subtitle: 'Architecture backend distribuée',
      description:
        'Conception en équipe de quatre personnes de services d’authentification et de gestion des joueurs avec API REST, bases PostgreSQL séparées, communication OpenFeign, règles métier et orchestration Docker.',
      technologies: ['Java', 'Spring Boot', 'PostgreSQL', 'Docker'],
      status: 'Projet en équipe'
    }
  ]
};

export const experiences = {
  en: [
    {
      role: 'Software Engineer Apprentice',
      company: 'Malakoff Humanis',
      date: 'Sep 2025 – Present',
      location: 'Guyancourt, France',
      bullets: [
        'Develop and maintain Java 21 and Spring Batch workflows using reader, processor, writer, and decider components, backed by MariaDB and SQL.',

        'Deliver end-to-end enhancements for business reporting, operational workflows, and communications, from requirements and data transformations through testing and integration.',

        'Contribute to the migration of SAS processing workflows to a shared Java architecture; analyze a legacy PHP production-supervision application and document development and maintenance practices.'
      ]
    },

    {
      role: 'DevSecOps Intern',
      company: 'Thales',
      date: 'Mar 2025 – Jun 2025',
      location: 'Lambersart, France',
      bullets: [
        'Built a Python tool delivered as a CLI, FastAPI REST API, and reusable package to generate and audit firewall configuration matrices.',

        'Reduced a manual process from approximately five days to under 2.5 seconds.',

        'Achieved 84% automated test coverage, a Pylint score of 9.70/10, and zero Bandit findings; integrated Jenkins, Docker, SonarQube, and Artifactory and authored Sphinx documentation.'
      ]
    },

    {
      role: 'Full-Stack Developer Intern',
      company: 'Extramus.eu',
      date: 'May 2023 – Sep 2023',
      location: 'Terranova di Sibari, Italy',
      bullets: [
        'Designed a multilingual booking application with a React single-page frontend and Laravel REST API; modelled the database and defined functional requirements.',

        'Implemented authentication, role-based access, and booking workflows, and produced multilingual technical and user documentation within an international team.'
      ]
    }
  ],

  fr: [
    {
      role: 'Ingénieur d’études et développement en alternance',
      company: 'Malakoff Humanis',
      date: 'Sept. 2025 – Aujourd’hui',
      location: 'Guyancourt, France',
      bullets: [
        'Développement et maintenance de traitements Java 21 et Spring Batch reposant sur des composants reader, processor, writer et decider, avec MariaDB et SQL.',

        'Livraison d’évolutions de bout en bout pour le reporting métier, les workflows opérationnels et les communications, de l’analyse des besoins et des transformations de données jusqu’aux tests et à l’intégration.',

        'Contribution à la migration de traitements SAS vers une architecture Java commune ; analyse d’une application legacy PHP de supervision de production et documentation des pratiques de développement et de maintenance.'
      ]
    },

    {
      role: 'Stagiaire DevSecOps',
      company: 'Thales',
      date: 'Mars 2025 – Juin 2025',
      location: 'Lambersart, France',
      bullets: [
        'Développement d’un outil Python distribué sous forme de CLI, d’API REST FastAPI et de package réutilisable pour générer et auditer des matrices de configuration pare-feu.',

        'Réduction d’un processus manuel d’environ cinq jours à moins de 2,5 secondes.',

        'Obtention de 84 % de couverture de tests automatisés, d’un score Pylint de 9,70/10 et d’aucun problème détecté par Bandit ; intégration de Jenkins, Docker, SonarQube et Artifactory et rédaction de documentation Sphinx.'
      ]
    },

    {
      role: 'Développeur Full-Stack stagiaire',
      company: 'Extramus.eu',
      date: 'Mai 2023 – Sept. 2023',
      location: 'Terranova di Sibari, Italie',
      bullets: [
        'Conception d’une application multilingue de réservation avec une interface React monopage et une API REST Laravel ; modélisation de la base de données et définition des besoins fonctionnels.',

        'Implémentation de l’authentification, du contrôle d’accès par rôles et des workflows de réservation, avec rédaction d’une documentation technique et utilisateur multilingue au sein d’une équipe internationale.'
      ]
    }
  ]
};

export const skills = {
  en: [
    {
      title: 'Programming',
      items: [
        'Java',
        'Python',
        'Rust',
        'JavaScript / TypeScript',
        'SQL',
        'Bash'
      ]
    },

    {
      title: 'Backend and data',
      items: [
        'Spring Boot',
        'Spring Batch',
        'FastAPI',
        'Django',
        'PostgreSQL',
        'MariaDB'
      ]
    },

    {
      title: 'Software engineering',
      items: [
        'Data structures',
        'Algorithms',
        'Object-oriented design',
        'Software design',
        'REST APIs',
        'Microservices',
        'Automated testing'
      ]
    },

    {
      title: 'Engineering tools',
      items: [
        'Git',
        'Linux',
        'Docker',
        'Jenkins',
        'SonarQube',
        'JUnit / pytest'
      ]
    }
  ],

  fr: [
    {
      title: 'Programmation',
      items: [
        'Java',
        'Python',
        'Rust',
        'JavaScript / TypeScript',
        'SQL',
        'Bash'
      ]
    },

    {
      title: 'Backend et données',
      items: [
        'Spring Boot',
        'Spring Batch',
        'FastAPI',
        'Django',
        'PostgreSQL',
        'MariaDB'
      ]
    },

    {
      title: 'Génie logiciel',
      items: [
        'Structures de données',
        'Algorithmique',
        'Conception orientée objet',
        'Conception logicielle',
        'API REST',
        'Microservices',
        'Tests automatisés'
      ]
    },

    {
      title: 'Outils d’ingénierie',
      items: [
        'Git',
        'Linux',
        'Docker',
        'Jenkins',
        'SonarQube',
        'JUnit / pytest'
      ]
    }
  ]
};